import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Timeout to detect when scrolling has stopped
   * @type {number | null}
   */
  #scrollStopTimer = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 800; // was 350, now 800ms for an even gentler hide
  #mobileHideDelay = 1800; // ms, even longer delay for mobile/touch
  #isTouchDevice = false;
  // Detect touch device
  // ...existing code...

  // ...existing code...

  #setTouchDevice = () => {
    this.#isTouchDevice = true;
    window.removeEventListener('touchstart', this.#setTouchDevice);
  };

  /**
   * Whether auto-hide on inactivity is enabled for the header
   * @type {boolean}
   */
  #autoHideEnabled = false;

  /**
   * Delay before auto-hiding the header after inactivity (in milliseconds)
   * @constant {number}
   */
  #autoHideDelay = 3000;

  /**
   * Timeout ID used to track inactivity
   * @type {number | null}
   */
  #inactivityTimer = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #handleAutoHideInteraction = () => {
    if (!this.#autoHideEnabled) return;

    this.removeAttribute('data-auto-hidden');
    this.#scheduleAutoHide();
  };

  #handleHeaderEngaged = () => {
    if (!this.#autoHideEnabled) return;

    this.#clearAutoHideTimer();
    this.removeAttribute('data-auto-hidden');
  };

  #handleHeaderDisengaged = () => {
    if (!this.#autoHideEnabled) return;

    this.#handleAutoHideInteraction();
  };

  #handleMouseEnter = () => {
    if (!this.#autoHideEnabled) return;
    // User is actively hovering — cancel any pending hide timer
    this.#clearAutoHideTimer();
    this.removeAttribute('data-auto-hidden');
  };

  #handleMouseLeave = () => {
    if (!this.#autoHideEnabled) return;
    // User stopped hovering — start the inactivity countdown from now
    if (this.#shouldAutoHide()) this.#scheduleAutoHide();
  };

  #scheduleAutoHide = () => {
    if (!this.#autoHideEnabled) return;

    this.#clearAutoHideTimer();
    if (!this.#shouldAutoHide()) return;

    this.#inactivityTimer = window.setTimeout(() => {
      if (this.#shouldAutoHide()) {
        this.setAttribute('data-auto-hidden', 'true');
      }
      this.#inactivityTimer = null;
    }, this.#autoHideDelay);
  };

  #clearAutoHideTimer = () => {
    if (this.#inactivityTimer !== null) {
      window.clearTimeout(this.#inactivityTimer);
      this.#inactivityTimer = null;
    }
  };

  #resetAutoHideState = () => {
    this.#clearAutoHideTimer();
    this.removeAttribute('data-auto-hidden');
  };

  #shouldAutoHide = () => {
    if (!this.#autoHideEnabled) return false;
    const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    if (scrollTop <= 0) return false; // never auto-hide at real top
    return this.dataset.stickyState === 'active';
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    if (this.#autoHideEnabled) {
      this.removeAttribute('data-auto-hidden');
      this.#clearAutoHideTimer();

      if (this.#scrollStopTimer !== null) clearTimeout(this.#scrollStopTimer);
      this.#scrollStopTimer = window.setTimeout(() => {
        this.#scrollStopTimer = null;
        if (this.#shouldAutoHide()) this.#scheduleAutoHide();
      }, 400);
    }

    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
        const scrollTop = document.scrollingElement?.scrollTop ?? 0;

        if (stickyMode === 'scroll-up') {
          // In scroll-up mode the wrapper remains sticky to avoid iOS Safari flicker,
          // so intersection alone no longer tells us when sticky behavior should engage.
          this.#offscreen = scrollTop > 0;
        }

        // Always handle reaching the top before the early-return guard — otherwise
        // scroll-up mode returns early and stickyState stays 'active' at scrollTop=0,
        // which keeps the background visible instead of going transparent.
        if (scrollTop <= 0) {
          this.dataset.stickyState = 'inactive';
          this.dataset.atTop = 'true';
          this.dataset.scrollDirection = 'none';
          this.removeAttribute('data-animating');
          this.removeAttribute('data-auto-hidden');
          this.#clearAutoHideTimer();
          this.#offscreen = false;
          this.#lastScrollTop = scrollTop;
          return;
        }

        if (!this.#offscreen && stickyMode !== 'always') return;

        const isScrollingUp = scrollTop < this.#lastScrollTop;
        const isAtTop = scrollTop <= 1;

        // If at top (within 1px), set stickyState to inactive
        if (isAtTop) {
          this.dataset.stickyState = 'inactive';
          this.dataset.atTop = 'true';
          this.dataset.scrollDirection = 'none';
          this.removeAttribute('data-animating');
          this.#offscreen = false;
          this.#lastScrollTop = scrollTop;
          return;
        }

        this.dataset.atTop = 'false';

        if (this.#timeout) {
          clearTimeout(this.#timeout);
          this.#timeout = null;
        }

        if (stickyMode === 'always') {
          this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
          this.dataset.stickyState = 'active';
          if (this.#autoHideEnabled) {
            if (this.#shouldAutoHide()) {
              this.#scheduleAutoHide();
            } else {
              this.#resetAutoHideState();
            }
          }
          this.#lastScrollTop = scrollTop;
          return;
        }

        if (isScrollingUp) {
          this.removeAttribute('data-animating');
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
        } else if (this.dataset.stickyState === 'active') {
          this.dataset.scrollDirection = 'none';
          this.setAttribute('data-animating', '');
          const delay = this.#isTouchDevice ? this.#mobileHideDelay : this.#animationDelay;
          this.#timeout = setTimeout(() => {
            this.dataset.stickyState = 'idle';
            this.removeAttribute('data-animating');
          }, delay);
        } else {
          this.dataset.scrollDirection = 'none';
          this.dataset.stickyState = 'idle';
        }

        if (this.#autoHideEnabled) {
          if (this.#shouldAutoHide()) {
            this.#scheduleAutoHide();
          } else {
            this.#resetAutoHideState();
          }
        }

        this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#autoHideEnabled = this.hasAttribute('data-auto-hide-inactive');
    const initialScrollTop = document.scrollingElement?.scrollTop ?? 0;
    const initialIsAtTop = this.getBoundingClientRect().top >= 0 && initialScrollTop <= 0;
    this.dataset.atTop = initialIsAtTop ? 'true' : 'false';
    if (this.#autoHideEnabled) {
      // Only respond to keyboard interactions and focus changes.
      // Pointer/mouse/touch interactions previously caused the header to
      // reappear when the cursor moved; restrict re-showing to scroll
      // and explicit keyboard/focus events for better UX.
      document.addEventListener('keydown', this.#handleAutoHideInteraction, true);
      this.addEventListener('focusin', this.#handleHeaderEngaged);
      this.addEventListener('focusout', this.#handleHeaderDisengaged);
      this.addEventListener('mouseenter', this.#handleMouseEnter);
      this.addEventListener('mouseleave', this.#handleMouseLeave);
      // Start inactivity timer immediately on page load
      this.#scheduleAutoHide();
    }

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      // Seed sticky state so data-auto-hidden CSS rule ([data-sticky-state]) fires even at page top
      if (!this.dataset.stickyState) this.dataset.stickyState = 'inactive';

      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#autoHideEnabled) {
      document.removeEventListener('keydown', this.#handleAutoHideInteraction, true);
      this.removeEventListener('focusin', this.#handleHeaderEngaged);
      this.removeEventListener('focusout', this.#handleHeaderDisengaged);
      this.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.removeEventListener('mouseleave', this.#handleMouseLeave);
      this.#resetAutoHideState();
      this.#autoHideEnabled = false;
    }

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#scrollStopTimer !== null) {
      clearTimeout(this.#scrollStopTimer);
      this.#scrollStopTimer = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
